import { format } from 'date-fns'
import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { useDialog } from '@last/core-ui/paprika'

import { useEmployees } from '@/composables/useEmployees'
import { managerConfirmation } from '@/plugins/managerConfirmation'
import { useToast } from '@/plugins/toast/toast'
import { useAuthStore } from '@/store/auth'
import { useConfigStore } from '@/store/config'
import { useTillStore } from '@/store/till'
import sync from '@/sync/service'
import type { Employee, EmployeeSelectionType } from '@/types'

export const useEmployeeStore = defineStore('employee', () => {
  const dialog = useDialog()
  const { t } = useI18n()
  const router = useRouter()
  const toast = useToast()

  const authStore = useAuthStore()
  const { selectEmployee, hasPrivilege } = authStore
  const { posAccessEmployees } = storeToRefs(authStore)

  const tillStore = useTillStore()
  const { setTill } = tillStore
  const { selectedCashTillIsStarted, askForForTillStartAmount, selectedCashTill, shiftsEnabled } =
    storeToRefs(tillStore)

  const configStore = useConfigStore()
  const { tills, config } = storeToRefs(configStore)

  const currentlySelectedCashTill = ref<string>(selectedCashTill.value?.id || '')
  const clockingEmployee = ref<Employee>()
  const safeSelectAfterwards = ref<boolean>(false)
  const isAttendanceMode = ref<boolean>(false)

  const { getColorClass, shortName, hasClockedIn } = useEmployees()

  const loginMode = computed<EmployeeSelectionType>(() =>
    isAttendanceMode.value ? 'names' : config.value.employees.selectionType
  )

  const shiftStarted = computed(() => !(shiftsEnabled.value && !selectedCashTillIsStarted.value))

  function canStartShift(employee: Employee) {
    return hasPrivilege(employee.id, 'SHIFT_MANAGER')
  }

  function isCheckedIn(employee: Employee): boolean {
    if (!config.value.lastProductAttendanceEnabled) return true

    return (employee.clockIn && !employee.clockOut) || false
  }

  async function safeSelectEmployee(employee: Employee): Promise<boolean> {
    if (!isCheckedIn(employee)) {
      clockingEmployee.value = employee
      return false
    } else if (shiftStarted.value || canStartShift(employee)) {
      await selectEmployeeAndContinue(employee.id)
      return true
    } else {
      toast({
        title: t('employees.not-shift-starter')
      })
      return false
    }
  }

  async function selectEmployeeAndContinue(employeeId: string) {
    saveSelectedTill()
    selectEmployee(employeeId)
    if (askForForTillStartAmount.value) {
      if (
        config.value.employees.selectionType === 'pin' ||
        (await managerConfirmation('SHIFT_MANAGER'))
      ) {
        void router.push({ name: 'startShift' })
      }
    } else {
      void router.push({ name: 'pos' })
    }
  }

  function saveSelectedTill() {
    const cashTill = tills.value.cash.find(till => till.id === currentlySelectedCashTill.value)
    setTill(cashTill!)
  }

  function saveAttendance({
    employee,
    type
  }: {
    employee: Employee
    type: 'clock-in' | 'clock-out'
  }) {
    const now = new Date()
    if (type === 'clock-in') {
      sync.record('employeeClockIn', {
        employeeId: employee.id,
        timestamp: now
      })
    } else {
      sync.record('employeeClockOut', {
        employeeId: employee.id,
        timestamp: now
      })
    }

    dialog({
      title: t(`employees.attendance.${type === 'clock-in' ? 'welcome' : 'goodbye'}`, {
        name: employee.name
      }),
      content: formatClockInTime(now),
      noLabels: true
    })
    clockingEmployee.value = undefined
  }

  function pinEntered(pin: string) {
    posAccessEmployees.value.some((employee: Employee) => {
      if (pin === employee.accessPin) {
        return safeSelectEmployee(employee)
      }
    })
  }

  function formatClockInTime(date: Date) {
    return format(date, 'dd/MM - hh:mm')
  }

  return {
    loginMode,
    shiftStarted,
    canStartShift,
    isCheckedIn,
    getColorClass,
    shortName,
    hasClockedIn,
    safeSelectEmployee,
    saveAttendance,
    pinEntered,
    formatClockInTime,
    currentlySelectedCashTill,
    clockingEmployee,
    isAttendanceMode,
    safeSelectAfterwards
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useEmployeeStore, import.meta.hot))
}
